import {
  BillingPeriod,
  CacheLocation,
  ChargeBeePlanId,
  PlanName,
  Support,
  PlanTier,
  ChargeBeeAddonId,
} from '@prerender/billing-shared';

import { DEFAULT_DESKTOP_USER_AGENT, DEFAULT_MOBILE_USER_AGENT } from '@prerender/prerender-shared/FE';
import Role from './Role';
import { FeatureFlag } from '../../hooks/FeatureFlag';

import type { PrerenderUserResponse } from './PrerenderUserResponse';

export type PrerenderUser = Omit<
  PrerenderUserResponse,
  | 'isBetaTester'
  | 'chargeBeePlanId'
  | 'csmId'
  | 'lastCharged'
  | 'nextBillingAt'
  | 'signupDate'
  | 'companyCreationDate'
  | 'trackingCodeInstalledDate'
  | 'numberOfNewNotifications'
  | 'upgradedAt'
  | 'lastSeen'
  | 'trialEndsAt'
  | 'cancelledAt'
> & {
  chargebeePlanId: ChargeBeePlanId;
  lastCharged: Date;
  nextBillingAt: Date | null;
  signupDate: Date; // Always returned from backend
  companyCreationDate: Date; // Always returned from backend
  trackingCodeInstalledDate: Date | null;
  upgradedAt: Date | null;
  hasSession: boolean;
  inProgress: boolean;
  showOnboarding: boolean;
  unreadNotifications: number;
  lastSeen: Date;
  trialEndsAt: Date | null;
  cancelledAt: Date | null;
};

export const defaultUser: PrerenderUser = {
  adminEmail: 'default-email@company.com',
  allURLCount: 0,
  amountOf429Renders: 0,
  billingPeriod: BillingPeriod.monthly,
  cacheFreshness: 14,
  cancelledAt: null,
  chargebeePlanId: ChargeBeePlanId.Starter,
  companyId: 1,
  companyName: 'default-email@company.com',
  costPerMonth: 0,
  customCachedPagesLimit: null,
  customPrice: null,
  customRecacheDelay: null,
  email: 'default-email@company.com',
  emailVerified: true,
  features: [
    FeatureFlag.Oversight,
    FeatureFlag.DomainsDetails,
    FeatureFlag.DomainSchedulerSettings,
    FeatureFlag.SeoReport,
  ],
  firstName: 'Firstname',
  gettingStartedSteps: {
    integrationWizardCompleted: true,
    queryParameters: false,
    reviewSitemaps: false,
    teamInvited: false,
  },
  hadPaidPlan: false,
  hadTrialBefore: false,
  hasBillingAddress: false,
  hasBillingInfo: false,
  hasBillingManager: false,
  hasCustomContractPricing: false,
  hasSession: false,
  id: 1,
  ignoreAllQueryParams: false,
  inProgress: false,
  isAdmin: true,
  isCacheFreshnessDisabled: false,
  isMeteredBilling: true,
  isTrial: false,
  lastCharged: new Date('Fri May 03 2024 12:15:29 GMT+0200'),
  lastName: null,
  lastSeen: new Date('Fri May 03 2024 12:15:29 GMT+0200'),
  mobileAdaptive: true,
  nextBillingAt: new Date('Fri April 03 2024 12:15:29 GMT+0200'),
  numPagesCached: 0,
  plan: {
    cacheRefreshInterval: [3, 3],
    current: {
      name: PlanName.Starter,
      chargeBeePlanId: ChargeBeePlanId.Starter,
      index: 0,
      legacy: false,
      costInCents: 34900,
      addonCostInCents: 125,
      currencyCode: 'USD',
      includedCaches: 500000,
      chargeBeeAddonId: ChargeBeeAddonId.starter2025Overages,
      suggested: false,
      current: false,
      cacheRefreshInterval: [0.25, 30],
      cacheLocation: CacheLocation.both,
      sitemapRevisitInterval: [8, 168],
      dataRetentionDays: 730,
      available: false,
      support: Support.high,
      billingPeriod: BillingPeriod.monthly,
      customPlan: false,
      contactUsToUse: false,
      tier: PlanTier.P2025,
      apiAccess: true,
      auditLog: true,
      domainSchedulerSettingsMaxRules: 5,
      dashboardAlerts: true,
      searchPerformanceMaxDomains: 0,
      id: ChargeBeePlanId.Starter,
      cacheLimit: null,
    },
    estimate: {
      addonsPriceInCents: 0,
      currencyCode: 'USD',
      packagesPriceInCents: 0,
      planPriceInCents: 0,
      totalInCents: 0,
    },
    limit: 1000,
    name: PlanName.Starter,
    suggested: null,
  },
  planName: PlanName.Starter,
  queryParamsWhitelist: true,
  recacheDelay: 60000,
  recachingDisabled: false,
  renderCounter: 0,
  role: Role.Guest,
  settings: {
    customDesktopUserAgent: DEFAULT_DESKTOP_USER_AGENT,
    customMobileUserAgent: DEFAULT_MOBILE_USER_AGENT,
    customMobileViewport: {
      height: 667,
      width: 375,
    },
    customViewport: {
      height: 718,
      width: 1440,
    },
    emailPreferences: {
      emailReportPeriod: 'WEEKLY',
      extraRenderEmailOptIn: true,
      sendSuggested: true,
      send429Alerts: true,
      aiDiscoverabilityStatusUpdate: true,
    },
    forceHTTPS: true,
    loadImages: false,
    manualReactivate: false,
    pageLoadTimeout: 20000,
    renderRegion: 'shared',
    statusCodesToCache: [],
  },
  showOnboarding: false,
  showRegistrationSteps: false,
  signupDate: new Date('Fri May 03 2024 12:15:29 GMT+0200'),
  companyCreationDate: new Date('Fri May 03 2024 12:15:29 GMT+0200'),
  token: 'aaaaaa',
  trackingCodeInstalled: false,
  trackingCodeInstalledDate: null,
  trialEndsAt: null,
  unreadNotifications: 0,
  upgradedAt: null,
  upgradedFromChargebeePlanId: null,
  userPausedRecaching: false,
  customDeal: null,
  onboardingSurveyCompleted: false,
};
